import { HeadingProps } from "@/components/heading";
import { HazardProps } from "@/components/mobile-card-hazard";

type HazardPropsCollection = HazardProps[];

export const Hazards: HazardPropsCollection = [
  {
    id: 0,
    name: "softStory",
    title: "Soft story",
    description:
      "Soft story buildings that haven't been reinforced may be at risk in an earthquake.",
    info: [
      "A soft story building is a structure built before 1978 that contains an open-floor level, such as a garage or retail space, below one or more living spaces.",
      "According to DataSF, there are 4,943 soft story buildings in SF. But not all are at risk!",
      "Some have been retrofitted to reinforce their lower levels and are generally considered earthquake-safe.",
      "Our data doesn't currently include permit history, so some buildings flagged as at risk may have already been upgraded.",
    ],
    link: {
      label: "Soft story dataset",
      url: "https://data.sfgov.org/Housing-and-Buildings/Soft-Story-Properties/beah-shgi/about_data",
    },
    icon: "circle",
    iconColor: "grey.400",
  },
  {
    id: 1,
    name: "liquefaction",
    title: "Liquefaction zones",
    description:
      "These areas are on less stable ground, which can result in shifting or sinking during an earthquake.",
    info: [
      "Liquefaction is a condition where soil turns into a liquid-like state under pressure.",
      "Buildings in liquefaction zones are at risk of sinking, tilting, or collapsing in a major earthquake.",
      "This risk can be lowered with seismic upgrades such as foundation reinforcement or drainage systems.",
    ],
    link: {
      label: "Liquefaction dataset",
      url: "https://data.sfgov.org/Geographic-Locations-and-Boundaries/Soil-Liquefaction-Hazard-Zone/i4t7-35u3/about_data",
    },
    icon: "square",
    iconColor: "orange",
  },
  {
    id: 2,
    name: "tsunami",
    title: "Tsunami zones",
    description:
      "These coastal areas are at risk of flooding and authorities may recommend evacuation in the event of a tsunami.",
    info: [
      "Buildings in tsunami zones could experience flooding from waves generated by a large earthquake.",
      "Typically, buildings located inland from the coast are less at risk in the event of a tsunami.",
      "Building an emergency kit and planning an evacuation route can help coastal residents be prepared for a tsunami.",
    ],
    link: {
      label: "Tsunami dataset",
      url: "https://www.conservation.ca.gov/cgs/tsunami/maps",
    },
    icon: "square",
    iconColor: "tsunamiBlue",
  },
];

export const Info = [
  {
    id: 1,
    name: "preparedness",
    title: "Preparedness",
    textStyle: "bold",
    list: [
      {
        id: 1,
        title: "Ready.gov",
        subtitle: "Earthquake guide",
        url: "https://www.ready.gov/",
      },
      {
        id: 2,
        title: "Earthquake Authority",
        url: "https://www.earthquakeauthority.com/",
      },
      {
        id: 3,
        title: "MyShake app",
        subtitle: "Early warning",
        url: "https://myshake.berkeley.edu/",
      },
    ],
  },
  {
    id: 2,
    name: "services",
    title: "Building retrofit services",
    list: [
      {
        id: 1,
        title: "Find a contractor",
        url: "https://www.californiaresidentialmitigationprogram.com/resources/find-a-contractor/",
      },
    ],
  },
  {
    id: 3,
    name: "grants",
    title: "Retrofit grants",
    list: [
      {
        id: 1,
        title: "Earthquake Brace + Bolt program",
        url: "https://www.californiaresidentialmitigationprogram.com/our-seismic-retrofit-programs/the-retrofits/ebb-retrofit",
      },
      {
        id: 2,
        title: "EarthquakeSoftStory.com",
        url: "https://www.californiaresidentialmitigationprogram.com/our-seismic-retrofit-programs/the-retrofits/ess-retrofit",
      },
    ],
  },
];

type HeadingPropsCollection = Record<string, HeadingProps>;

export const Headings: HeadingPropsCollection = {
  home: {
    text: "How safe is your home in an earthquake?",
    highlight: "How safe ",
    style: {
      color: "white",
      whiteSpace: { base: "normal", md: "pre-line" },
    },
    highlightStyle: { color: "yellow" },
    themeTextStyle: "headerMedium",
  },
  about: {
    text: "Earthquake safety starts with knowledge.\nWe're here to help.",
    highlight: "",
    style: { color: "blue.text", fontWeight: "light" },
    highlightStyle: {},
    themeTextStyle: "headerBig",
  },
};

export const mockAddressHazardData = [
  { exists: false, last_updated: null },
  { exists: true, last_updated: null },
  { exists: false, last_updated: null },
];

export const DataInfoLinks = [
  {
    id: 1,
    name: "softStory",
    label: "Soft Story Dataset",
    url: "https://data.sfgov.org/Housing-and-Buildings/Soft-Story-Properties/beah-shgi/about_data",
  },
  {
    id: 2,
    name: "liquefaction",
    label: "Liquefaction Dataset",
    url: "https://data.sfgov.org/Geographic-Locations-and-Boundaries/Soil-Liquefaction-Hazard-Zone/i4t7-35u3/about_data",
  },
  {
    id: 3,
    name: "tsunami",
    label: "Tsunami Dataset",
    url: "https://www.conservation.ca.gov/cgs/tsunami/maps",
  },
];

export const TeamMembers = [
  {
    role: "Project Manager, Data Science",
    name: "Leela Solomon",
  },
  {
    role: "Assistant Project Manager, Data Science Lead",
    name: "Oscar Syu",
  },
  {
    role: "UX/UI Design Lead",
    name: "Ramya Rajasekaran",
  },
  {
    role: "Partnerships Lead",
    name: "Julia Cordero",
  },
  {
    role: "Back End Engineering Lead",
    name: "Anna Gennadinik",
  },
  {
    role: "Front End Engineering Lead",
    name: "Nick Visutsithiwong",
  },
  {
    role: "Front End Engineering Lead",
    name: "Josh van Eyken",
  },
  {
    role: "UX/UI Design",
    name: "Jocelyn Su",
  },
  {
    role: "UX Writer",
    name: "Nikki Collister",
  },
  {
    role: "Front End Engineer",
    name: "Jobe Abdellah",
  },
  {
    role: "Front End Engineer",
    name: "Jesse Mavi",
  },
  {
    role: "Back End Engineer",
    name: "Adam Finkle",
  },
  {
    role: "Data Science, Back End Engineer",
    name: "Lola Jackson",
  },
  {
    role: "Back End Engineer, Data Science",
    name: "Jiang John",
  },
  {
    role: "Partnerships",
    name: "Chanel Johnson",
  },
  {
    role: "Data Science",
    name: "Amreen Kaur",
  },
  {
    role: "UX/UI Design",
    name: "Teresa Yang",
  },
  {
    role: "UX/UI Design",
    name: "Ruby Hirsch",
  },
  {
    role: "Front End Engineer, Back End Engineer",
    name: "Zaima Choudhry",
  },
];

export const InactiveTeamMembers = [
  {
    role: "Product Design Lead",
    name: "Micah Johnson",
  },
  {
    role: "Engineering Lead",
    name: "Eli Lucherini ",
  },
  {
    role: "Engineering Lead",
    name: "Svetlana Eliseeva",
  },
  {
    role: "Front End Engineer",
    name: "Amna Khan",
  },
  {
    role: "Front End Engineer",
    name: "Diya Baliga",
  },
  {
    role: "Front End Engineer",
    name: "Leah Treidler",
  },
  {
    role: "Full Stack Engineer",
    name: "Raymond Yee",
  },
  {
    role: "Full Stack Engineer",
    name: "Vincent Shuali",
  },
  {
    role: "Back End Engineer",
    name: "Neeraj Lad",
  },
  {
    role: "Data Science",
    name: "Peter Cuddihy",
  },
];

export const PillData = [
  {
    name: "softStory",
    trueData: "Non-Compliant",
    falseData: "Compliant",
    noData: "No Data",
  },
  {
    name: "liquefaction",
    trueData: "Susceptible",
    falseData: "Not in Zone",
    noData: "No Data",
  },
  {
    name: "tsunami",
    trueData: "In Zone",
    falseData: "Not in Zone",
    noData: "No Data",
  },
];

export const LayerIds = ["softStoriesLayer", "seismicLayer", "tsunamiLayer"];
